import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { catchError, debounceTime, distinctUntilChanged, finalize, retry } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subject, Subscription, of } from 'rxjs';
import { CategoryItem, SubCategoryItem } from 'src/app/request-v2/models/create-request.dto';
import { RequestsV2ImplService } from 'src/app/request-v2/services/requests-v2.impl.service';

@Component({
    selector: 'app-category-selection',
    templateUrl: './category-selection.component.html',
    styleUrls: ['./category-selection.component.scss'],
})
export class CategorySelectionComponent implements OnInit, OnDestroy {
    @Input() categories: CategoryItem[] = [];

    @Input() loading = false;

    @Input() selectedCategory: CategoryItem | null = null;

    @Output() categorySelected = new EventEmitter<CategoryItem>();

    @ViewChild('categoriesContainer') categoriesContainer: ElementRef;

    searchQuery = '';

    filteredCategories: CategoryItem[] = [];

    allCategories: CategoryItem[] = [];

    private searchSubject = new Subject<string>();

    private searchSubscription: Subscription;

    constructor(
        private requestsService: RequestsV2ImplService,
        private translate: TranslateService,
    ) {}

    ngOnInit(): void {
        this.setupSearchSubscription();
        this.loadCategories();
    }

    ngOnDestroy(): void {
        if (this.searchSubscription) {
            this.searchSubscription.unsubscribe();
        }
    }

    setupSearchSubscription(): void {
        this.searchSubscription = this.searchSubject.pipe(debounceTime(300), distinctUntilChanged()).subscribe((query) => {
            this.filterCategoriesLocally(query);
        });
    }

    loadCategories(): void {
        this.loading = true;
        this.requestsService
            .getRequestCategories(this.searchQuery)
            .pipe(
                retry(1),
                catchError((error): Observable<CategoryItem[]> => {
                    console.error('Error loading categories:', error);
                    return of([]);
                }),
                finalize(() => {
                    this.loading = false;
                }),
            )
            .subscribe((response: CategoryItem[]) => {
                this.processCategories(response);
            });
    }

    processCategories(response: CategoryItem[]): void {
        this.allCategories = response.map((item: any) => {
            const subCategories: SubCategoryItem[] = [];
            if (item.subCategories && Array.isArray(item.subCategories)) {
                item.subCategories.forEach((sub: any) => {
                    subCategories.push({
                        _id: sub._id || '',
                        name: sub.name || '',
                        slaInDays: sub.slaInDays || null,
                        selected: false,
                    });
                });
            }
            const isSelected = this.selectedCategory && this.selectedCategory._id === item._id;
            return {
                _id: item._id || '',
                name: item.name || '',
                description: item.description || '',
                icon: item.icon || 'icon-map-marker',
                organisation: item.organisation || 'City',
                color: item.color || '#000000',
                order: item.order || 0,
                selected: isSelected,
                subCategories,
                department: item.department || undefined,
                assignedTo: item.assignedTo || undefined,
                slaInDays: item.slaInDays || null,
                statuses: item.statuses || undefined,
                forceLocation: item.forceLocation ?? true,
                isInternal: item.isInternal ?? false,
            };
        });
        this.filterCategoriesLocally(this.searchQuery);
    }

    onSearchChange(): void {
        this.searchSubject.next(this.searchQuery);
    }

    filterCategoriesLocally(query: string): void {
        const lowerCaseQuery = query.toLowerCase().trim();
        if (!lowerCaseQuery) {
            this.filteredCategories = [...this.allCategories];
        } else {
            this.filteredCategories = this.allCategories.filter(
                (category) => category.name.toLowerCase().includes(lowerCaseQuery) || (category.description && category.description.toLowerCase().includes(lowerCaseQuery)),
            );
        }
    }

    selectCategory(category: CategoryItem): void {
        this.allCategories = this.allCategories.map((cat) => ({
            ...cat,
            selected: cat._id === category._id,
        }));
        this.filteredCategories = this.filteredCategories.map((cat) => ({
            ...cat,
            selected: cat._id === category._id,
        }));

        this.selectedCategory = category;
        this.categorySelected.emit(category);
    }
}
